import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

export type UnitSystem = 'imperial' | 'metric';

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid';

export interface UserProfile {
  userId: string;
  email: string;
  name?: string;
  isAdmin: boolean;
  unitSystem: UnitSystem;  // 'imperial' (US) or 'metric' (SI)
  purchasedSets?: string[];  // e.g., ['pre-marriage']
  emailOptOut?: boolean;  // Opt out of transactional email notifications
  hasPurchased?: boolean;  // One-time payment flag (set by Stripe webhook)
  stripeCustomerId?: string;  // Stripe Customer ID (cus_xxx)
  subscriptionId?: string;  // Stripe Subscription ID (sub_xxx)
  subscriptionStatus?: SubscriptionStatus;
  subscriptionPriceId?: string;  // Stripe Price ID
  subscriptionCurrentPeriodEnd?: string;  // ISO date — when current billing period ends
  createdAt: string;
  updatedAt: string;
}

/**
 * Check if a user has purchased a specific question set
 */
export function hasPurchasedSet(profile: UserProfile | null, setId: string): boolean {
  return profile?.purchasedSets?.includes(setId) ?? false;
}

/**
 * Check if a user has admin privileges
 */
export async function isUserAdmin(userId: string): Promise<boolean> {
  try {
    // Validate inputs
    if (!userId || !db) {
      console.warn('isUserAdmin: Invalid userId or db not initialized');
      return false;
    }

    const userDocRef = doc(db, 'userProfiles', userId);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.isAdmin === true;
    }

    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Create or update user profile with admin flag
 * Names are stored as plaintext per Data Classification spec
 */
export async function createUserProfile(
  userId: string,
  email: string,
  isAdmin: boolean = false,
  name?: string,
  unitSystem: UnitSystem = 'imperial'  // Default to imperial (US system)
): Promise<void> {
  try {
    const userDocRef = doc(db, 'userProfiles', userId);

    await setDoc(userDocRef, {
      userId,
      email,
      ...(name && { name }),
      isAdmin,
      unitSystem,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdAtTimestamp: serverTimestamp(),
      updatedAtTimestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
}

/**
 * Get user profile
 * Names are stored as plaintext per Data Classification spec
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const userDocRef = doc(db, 'userProfiles', userId);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      let finalName = data.name;

      // Handle legacy encrypted name formats (convert to plaintext on read)
      if (data.name && typeof data.name === 'object') {
        // Old partial encryption format - extract first name
        finalName = data.name.firstNames || JSON.stringify(data.name);
      }

      return {
        userId: data.userId,
        email: data.email,
        name: finalName,
        isAdmin: data.isAdmin || false,
        unitSystem: data.unitSystem || 'imperial',  // Default to imperial for existing users
        purchasedSets: data.purchasedSets || undefined,
        emailOptOut: data.emailOptOut || undefined,
        hasPurchased: data.hasPurchased || undefined,
        stripeCustomerId: data.stripeCustomerId || undefined,
        subscriptionId: data.subscriptionId || undefined,
        subscriptionStatus: data.subscriptionStatus || undefined,
        subscriptionPriceId: data.subscriptionPriceId || undefined,
        subscriptionCurrentPeriodEnd: data.subscriptionCurrentPeriodEnd || undefined,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString()
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}

/**
 * Update user admin status (only callable by admins)
 */
export async function setUserAdminStatus(
  userId: string,
  isAdmin: boolean
): Promise<void> {
  try {
    const userDocRef = doc(db, 'userProfiles', userId);

    await updateDoc(userDocRef, {
      isAdmin,
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating admin status:', error);
    throw error;
  }
}

/**
 * Update user profile name
 * Names are stored as plaintext per Data Classification spec
 */
export async function updateUserName(
  userId: string,
  name: string
): Promise<void> {
  try {
    const userDocRef = doc(db, 'userProfiles', userId);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      // Update existing profile
      await updateDoc(userDocRef, {
        name,
        updatedAt: new Date().toISOString(),
        updatedAtTimestamp: serverTimestamp()
      });
    } else {
      // Profile doesn't exist - this shouldn't happen, but create it just in case
      const userProfile = await getUserProfile(userId);
      await createUserProfile(
        userId,
        userProfile?.email || '',
        userProfile?.isAdmin || false,
        name,
        userProfile?.unitSystem || 'imperial'
      );
    }
  } catch (error) {
    console.error('Error updating user name:', error);
    throw error;
  }
}

/**
 * Update user unit system preference
 */
export async function updateUserUnitSystem(
  userId: string,
  unitSystem: UnitSystem
): Promise<void> {
  try {
    const userDocRef = doc(db, 'userProfiles', userId);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      // Update existing profile
      await updateDoc(userDocRef, {
        unitSystem,
        updatedAt: new Date().toISOString(),
        updatedAtTimestamp: serverTimestamp()
      });
    } else {
      // Profile doesn't exist - this shouldn't happen, but create it just in case
      const userProfile = await getUserProfile(userId);
      await createUserProfile(
        userId,
        userProfile?.email || '',
        userProfile?.isAdmin || false,
        userProfile?.name,
        unitSystem
      );
    }
  } catch (error) {
    console.error('Error updating unit system:', error);
    throw error;
  }
}

/**
 * Update user email opt-out preference
 */
export async function updateEmailOptOut(
  userId: string,
  emailOptOut: boolean
): Promise<void> {
  try {
    const userDocRef = doc(db, 'userProfiles', userId);
    const docSnap = await getDoc(userDocRef);
    if (docSnap.exists()) {
      await updateDoc(userDocRef, {
        emailOptOut,
        updatedAt: new Date().toISOString(),
        updatedAtTimestamp: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating email opt-out:', error);
    throw error;
  }
}

/**
 * Update profile fields (name, unit system, email opt-out) in a single write.
 *
 * Firestore rules rate-limit userProfiles updates to one per 2 seconds, so the
 * previous three separate update calls would trip the limiter (and fail with a
 * 400) whenever more than one field changed at once. A single merge write avoids
 * that — one save action, one rule evaluation.
 */
export async function updateUserProfile(
  userId: string,
  fields: { name?: string; unitSystem?: UnitSystem; emailOptOut?: boolean }
): Promise<void> {
  try {
    const updates: Record<string, unknown> = {
      updatedAt: new Date().toISOString(),
      updatedAtTimestamp: serverTimestamp(),
    };
    if (fields.name !== undefined) updates.name = fields.name;
    if (fields.unitSystem !== undefined) updates.unitSystem = fields.unitSystem;
    if (fields.emailOptOut !== undefined) updates.emailOptOut = fields.emailOptOut;

    await setDoc(doc(db, 'userProfiles', userId), updates, { merge: true });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
}
